import * as THREE from 'three';
import type { AudioMetrics } from './audio-engine';

export type VisualSettings = {
  intensity: number;
  distortion: number;
  travel: number;
  orbSize: number;
  orbMorph: number;
  orbResponse: number;
  orbTurbulence: number;
  orbBassPulse: number;
  orbCohesion: number;
  orbTopology: number;
  orbFragmentation: number;
  bandHues: number[];
};

export type NebulaPreviewMode = 'ion-veil' | 'spectral-bloom' | 'spectral-stars';

type ShaderUniforms = {
  uTime: { value: number };
  uBands: { value: number[] };
  uIntensity: { value: number };
  uDistortion: { value: number };
  uTravel: { value: number };
  uOrbSize: { value: number };
  uOrbMorph: { value: number };
  uOrbResponse: { value: number };
  uOrbTurbulence: { value: number };
  uOrbBassPulse: { value: number };
  uOrbCohesion: { value: number };
  uOrbTopology: { value: number };
  uOrbFragmentation: { value: number };
  uSpatialDepth: { value: number };
  uBandHues: { value: number[] };
  uPixelRatio: { value: number };
};

export class AccretionVisualizer {
  private readonly container: HTMLElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 100);
  private readonly diskGroup = new THREE.Group();
  private readonly uniforms: ShaderUniforms;
  private readonly nebulaTimeUniform = { value: 0 };
  private nebulaPlaybackBlend = 0;
  private nebulaPlaying = false;
  private previewing = false;
  private reactionBlend = 0;
  private readonly reactiveBands = new Array<number>(8).fill(0);
  private readonly clock = new THREE.Clock();
  private metrics: AudioMetrics | null = null;
  private settings: VisualSettings = {
    intensity: 0.94,
    distortion: 0.38,
    travel: 0.62,
    orbSize: 1,
    orbMorph: 0.38,
    orbResponse: 1,
    orbTurbulence: 0.52,
    orbBassPulse: 1,
    orbCohesion: 0.55,
    orbTopology: 0,
    orbFragmentation: 0,
    bandHues: [190, 198, 207, 220, 270, 315, 348, 16].map((hue) => hue / 360),
  };
  private rotationTarget = { x: 0.03, y: 0 };
  private hoverAttitude = { x: 0, y: 0 };
  private gyrationEnabled = false;
  private gyrationFrom = { x: 0.03, y: 0 };
  private gyrationTo = { x: 0.03, y: 0 };
  private gyrationProgress = 0;
  private gyrationDuration = 7;
  private dragging = false;
  private pointer = { x: 0, y: 0 };
  private centerOrb: THREE.Points | null = null;
  private orbDeformationEnvelope = 0;
  private orbCueFloor = 0;
  private readonly previousOrbCueBands = new Array<number>(8).fill(0);
  private orbCueCooldown = 0;
  private raf = 0;
  private resizeObserver: ResizeObserver;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(
    canvas: HTMLCanvasElement,
    container: HTMLElement,
    nebulaMode: NebulaPreviewMode = 'spectral-bloom',
    private readonly spatialPreview = false,
  ) {
    this.container = container;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: 'high-performance',
      alpha: false,
    });
    this.renderer.setClearColor(0x000001, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));

    this.uniforms = {
      uTime: { value: 0 },
      uBands: { value: new Array<number>(8).fill(0) },
      uIntensity: { value: this.settings.intensity },
      uDistortion: { value: this.settings.distortion },
      uTravel: { value: this.settings.travel },
      uOrbSize: { value: this.settings.orbSize },
      uOrbMorph: { value: 0 },
      uOrbResponse: { value: this.settings.orbResponse },
      uOrbTurbulence: { value: this.settings.orbTurbulence },
      uOrbBassPulse: { value: this.settings.orbBassPulse },
      uOrbCohesion: { value: this.settings.orbCohesion },
      uOrbTopology: { value: this.settings.orbTopology },
      uOrbFragmentation: { value: this.settings.orbFragmentation },
      uSpatialDepth: { value: spatialPreview ? 3.5 : 1 },
      uBandHues: { value: [...this.settings.bandHues] },
      uPixelRatio: { value: this.renderer.getPixelRatio() },
    };

    this.scene.add(this.createNebula(nebulaMode));
    this.scene.add(this.createStars(nebulaMode === 'spectral-stars'));
    this.scene.add(this.diskGroup);
    this.buildDisk();
    this.buildCenterOrb();
    this.resetAttitude();
    this.bindInteraction();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.resize();
    this.animate();
  }

  setMetrics(metrics: AudioMetrics): void {
    this.metrics = metrics;
  }

  setPlaying(playing: boolean): void {
    this.nebulaPlaying = playing;
  }

  setPreviewing(previewing: boolean): void {
    this.previewing = previewing;
  }

  setGyration(enabled: boolean): void {
    this.gyrationEnabled = enabled;
    if (enabled) {
      this.gyrationFrom = { ...this.rotationTarget };
      this.chooseGyrationDestination();
      this.gyrationProgress = 0;
    }
  }

  setSettings(settings: Partial<VisualSettings>): void {
    this.settings = { ...this.settings, ...settings };
    this.uniforms.uIntensity.value = this.settings.intensity;
    this.uniforms.uDistortion.value = this.settings.distortion;
    this.uniforms.uTravel.value = this.settings.travel;
    this.uniforms.uOrbSize.value = this.settings.orbSize;
    this.uniforms.uOrbMorph.value = this.settings.orbMorph * this.orbDeformationEnvelope;
    this.uniforms.uOrbResponse.value = this.settings.orbResponse;
    this.uniforms.uOrbTurbulence.value = this.settings.orbTurbulence;
    this.uniforms.uOrbBassPulse.value = this.settings.orbBassPulse;
    this.uniforms.uOrbCohesion.value = this.settings.orbCohesion;
    this.uniforms.uOrbTopology.value = this.settings.orbTopology;
    this.uniforms.uOrbFragmentation.value = this.settings.orbFragmentation;
    this.uniforms.uBandHues.value = [...this.settings.bandHues];
  }

  resetAttitude(): void {
    this.rotationTarget.x = 0.03;
    this.rotationTarget.y = 0;
    this.hoverAttitude = { x: 0, y: 0 };
  }

  private chooseGyrationDestination(): void {
    this.gyrationTo = {
      x: THREE.MathUtils.lerp(-0.34, 0.86, Math.random()),
      y: THREE.MathUtils.lerp(-0.72, 0.72, Math.random()),
    };
    this.gyrationDuration = THREE.MathUtils.lerp(5.5, 9, Math.random());
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    this.resizeObserver.disconnect();
    this.renderer.dispose();
  }

  private buildDisk(): void {
    const count = window.innerWidth < 700 ? 9000 : 14600;
    const geometry = this.createParticleGeometry(count);
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.particleVertexShader(),
      fragmentShader: this.particleFragmentShader(),
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.NormalBlending,
    });
    const particles = new THREE.Points(geometry, material);
    particles.frustumCulled = false;
    this.diskGroup.add(particles);
  }

  // Center cloud inspired by Humprt/particula (MIT): volumetric particles,
  // frequency populations, procedural turbulence, and an outward beat impulse.
  private buildCenterOrb(): void {
    const count = window.innerWidth < 700 ? 4600 : 8200;
    const geometry = this.createCenterOrbGeometry(count);
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.centerOrbVertexShader(),
      fragmentShader: this.centerOrbFragmentShader(),
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    this.centerOrb = new THREE.Points(geometry, material);
    this.centerOrb.frustumCulled = false;
    this.centerOrb.renderOrder = 1;
    this.centerOrb.position.z = this.spatialPreview ? 0.24 : 0;
    this.diskGroup.add(this.centerOrb);
  }

  private createCenterOrbGeometry(count: number): THREE.BufferGeometry {
    const positions = new Float32Array(count * 3);
    const seed = new Float32Array(count);
    const band = new Float32Array(count);
    const size = new Float32Array(count);

    for (let index = 0; index < count; index += 1) {
      const azimuth = this.random(index * 5 + 31000) * Math.PI * 2;
      const vertical = this.random(index * 5 + 31001) * 2 - 1;
      const horizontal = Math.sqrt(Math.max(0, 1 - vertical * vertical));
      const radial = Math.cbrt(this.random(index * 5 + 31002)) * 0.58;
      positions[index * 3] = Math.cos(azimuth) * horizontal * radial;
      positions[index * 3 + 1] = vertical * radial;
      positions[index * 3 + 2] = Math.sin(azimuth) * horizontal * radial;
      seed[index] = this.random(index * 5 + 31003);
      band[index] = index % 8;
      size[index] = 0.82 + this.random(index * 5 + 31004) * 1.18;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    geometry.setAttribute('aBand', new THREE.BufferAttribute(band, 1));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    return geometry;
  }

  private createParticleGeometry(count: number): THREE.BufferGeometry {
    const positions = new Float32Array(count * 3);
    const radius = new Float32Array(count);
    const angle = new Float32Array(count);
    const seed = new Float32Array(count);
    const band = new Float32Array(count);
    const size = new Float32Array(count);

    for (let index = 0; index < count; index += 1) {
      const randomA = this.random(index * 6 + 1);
      const randomB = this.random(index * 6 + 2);
      const randomC = this.random(index * 6 + 3);
      const randomD = this.random(index * 6 + 4);
      const randomE = this.random(index * 6 + 5);
      const bandIndex = Math.min(7, Math.floor(randomA * 8));
      const laneCenter = 1.3 + bandIndex * 0.19;
      const laneSpread = randomD > 0.78 ? 0.3 : 0.13;
      const laneOffset = (randomC - 0.5) * laneSpread + Math.sin(randomE * Math.PI * 2) * 0.01;
      const radial = laneCenter + laneOffset;
      radius[index] = radial;
      angle[index] = randomB * Math.PI * 2 + radial * 1.1;
      seed[index] = randomE;
      band[index] = bandIndex;
      size[index] = 0.82 + this.random(index * 6 + 6) * 1.42;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aRadius', new THREE.BufferAttribute(radius, 1));
    geometry.setAttribute('aAngle', new THREE.BufferAttribute(angle, 1));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    geometry.setAttribute('aBand', new THREE.BufferAttribute(band, 1));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    return geometry;
  }

  private createStars(atmosphericPreview = false): THREE.Points {
    const count = atmosphericPreview ? 1040 : 360;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const phases = new Float32Array(count);
    for (let index = 0; index < count; index += 1) {
      let x = 0;
      let y = 0;
      let attempt = 0;
      do {
        x = (this.random(index * 7 + attempt * 2 + 20000) - 0.5) * 22;
        y = (this.random(index * 7 + attempt * 2 + 20001) - 0.5) * 13;
        attempt += 1;
      } while (atmosphericPreview && Math.hypot(x / 11, y / 6.5) < 0.34 && attempt < 7);
      positions[index * 3] = x;
      positions[index * 3 + 1] = y;
      const depth = this.random(index + 21000);
      positions[index * 3 + 2] = -2 - depth * 5;
      const temperature = this.random(index + 22000);
      const brightness = atmosphericPreview ? 0.37 + this.random(index + 23000) * 0.36 : 0.2;
      colors[index * 3] = brightness * (temperature > 0.82 ? 1 : 0.68);
      colors[index * 3 + 1] = brightness * (temperature < 0.7 ? 0.9 : 0.82);
      colors[index * 3 + 2] = brightness * (temperature < 0.7 ? 1 : 0.72);
      const anchor = this.random(index + 24000) > 0.9 ? 0.52 : 0;
      sizes[index] = atmosphericPreview ? 0.82 + (1 - depth) * 0.54 + anchor : 0.72;
      phases[index] = this.random(index + 25000) * Math.PI * 2;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: this.uniforms.uTime,
        uPixelRatio: this.uniforms.uPixelRatio,
        uOpacity: { value: atmosphericPreview ? 0.56 : 0.28 },
      },
      vertexShader: /* glsl */`
        attribute vec3 color;
        attribute float aSize;
        attribute float aPhase;
        uniform float uTime;
        uniform float uPixelRatio;
        varying vec3 vColor;
        varying float vTwinkle;

        void main() {
          vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
          float slowCycle = max(0.0, sin(uTime * 0.31 + aPhase));
          vTwinkle = pow(slowCycle, 8.0);
          vColor = color;
          gl_Position = projectionMatrix * viewPosition;
          gl_PointSize = (0.55 + aSize) * uPixelRatio * (1.0 + vTwinkle * 0.62);
        }
      `,
      fragmentShader: /* glsl */`
        precision highp float;
        uniform float uOpacity;
        varying vec3 vColor;
        varying float vTwinkle;

        void main() {
          float distanceToCenter = length(gl_PointCoord - 0.5);
          float halo = 1.0 - smoothstep(0.08, 0.5, distanceToCenter);
          float core = 1.0 - smoothstep(0.0, 0.16, distanceToCenter);
          float glint = halo * (0.66 + vTwinkle * 0.4) + core * vTwinkle * 0.46;
          gl_FragColor = vec4(vColor * (0.92 + vTwinkle * 0.44), glint * uOpacity);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    const stars = new THREE.Points(geometry, material);
    stars.renderOrder = -10;
    return stars;
  }

  private createNebula(mode: NebulaPreviewMode): THREE.Mesh {
    const geometry = new THREE.PlaneGeometry(2, 2, 1, 1);
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: this.nebulaTimeUniform,
        uBandHues: this.uniforms.uBandHues,
        uVariant: { value: mode === 'ion-veil' ? 0 : 1 },
      },
      vertexShader: /* glsl */`
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.999, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        precision highp float;
        varying vec2 vUv;
        uniform float uTime;
        uniform float uBandHues[8];
        uniform float uVariant;

        float bandHue(float index) {
          if (index < 0.5) return uBandHues[0];
          if (index < 1.5) return uBandHues[1];
          if (index < 2.5) return uBandHues[2];
          if (index < 3.5) return uBandHues[3];
          if (index < 4.5) return uBandHues[4];
          if (index < 5.5) return uBandHues[5];
          if (index < 6.5) return uBandHues[6];
          return uBandHues[7];
        }

        vec3 hsv2rgb(vec3 c) {
          vec3 p = abs(fract(c.xxx + vec3(0.0, 0.666667, 0.333333)) * 6.0 - 3.0);
          return c.z * mix(vec3(1.0), clamp(p - 1.0, 0.0, 1.0), c.y);
        }

        float hash21(vec2 point) {
          point = fract(point * vec2(123.34, 456.21));
          point += dot(point, point + 45.32);
          return fract(point.x * point.y);
        }

        float noise(vec2 point) {
          vec2 cell = floor(point);
          vec2 blend = fract(point);
          blend = blend * blend * (3.0 - 2.0 * blend);
          return mix(
            mix(hash21(cell), hash21(cell + vec2(1.0, 0.0)), blend.x),
            mix(hash21(cell + vec2(0.0, 1.0)), hash21(cell + vec2(1.0, 1.0)), blend.x),
            blend.y
          );
        }

        float fbm(vec2 point) {
          float value = 0.0;
          float amplitude = 0.55;
          mat2 rotation = mat2(0.82, -0.57, 0.57, 0.82);
          for (int octave = 0; octave < 6; octave++) {
            value += noise(point) * amplitude;
            point = rotation * point * 2.04 + vec2(8.7, 3.1);
            amplitude *= 0.48;
          }
          return value;
        }

        float ridged(vec2 point) {
          float ridge = 1.0 - abs(fbm(point) * 2.0 - 1.0);
          return ridge * ridge * ridge;
        }

        void main() {
          vec2 centered = vUv - 0.5;
          centered.x *= 1.55;
          float time = uTime * 0.014;
          float angle = atan(centered.y, centered.x) / 6.2831853 + 0.5;
          float bandPosition = fract(angle + 0.0625) * 8.0;
          float bandIndexA = floor(bandPosition);
          float bandIndexB = mod(bandIndexA + 1.0, 8.0);
          float bandMix = smoothstep(0.18, 0.82, fract(bandPosition));
          float localHue = mix(bandHue(bandIndexA), bandHue(bandIndexB), bandMix);
          vec3 spectralColor = hsv2rgb(vec3(localHue, 0.72, 0.72));
          float centralReserve = 0.045 + 0.955 * smoothstep(0.075, 0.42, length(centered));
          float rightCloudLift = smoothstep(-0.16, 0.7, centered.x);
          float edgeFade = smoothstep(0.0, 0.11, vUv.x)
            * (1.0 - smoothstep(0.89, 1.0, vUv.x))
            * smoothstep(0.0, 0.09, vUv.y)
            * (1.0 - smoothstep(0.91, 1.0, vUv.y));
          vec3 color = vec3(0.0);
          float alpha = 0.0;

          if (uVariant < 0.5) {
            mat2 lean = mat2(0.94, -0.34, 0.34, 0.94);
            vec2 p = lean * centered * 2.8;
            float farCloud = fbm(p * 0.54 + vec2(time * 0.36, -time * 0.22));
            vec2 warp = vec2(
              fbm(p * 0.72 + vec2(time * 0.65, 6.4)),
              fbm(p * 0.72 + vec2(-4.7, -time * 0.48))
            ) - 0.5;
            vec2 cloudP = p + warp * 1.72
              + vec2(time * 0.28, -time * 0.17);
            float body = fbm(cloudP);
            float innerBillow = fbm(cloudP * 1.92 - warp * 0.8);
            float filament = ridged(cloudP * vec2(2.7, 4.3) + vec2(-time * 0.8, time * 0.32));
            float microDetail = fbm(cloudP * 5.1 + warp * 2.4);
            float dustLane = smoothstep(0.48, 0.72, fbm(cloudP * 1.34 + vec2(8.1, -3.6)));
            float volume = smoothstep(
              0.32 - rightCloudLift * 0.055,
              0.7 - rightCloudLift * 0.035,
              body * 0.63 + innerBillow * 0.31 + farCloud * 0.24
            );
            volume *= mix(1.0, 0.28, dustLane * smoothstep(0.42, 0.76, innerBillow));
            float litEdge = filament * smoothstep(0.36, 0.78, body) * (0.42 + microDetail * 0.58);

            vec3 deepSpace = vec3(0.004, 0.025, 0.07);
            vec3 ionBlue = vec3(0.015, 0.36, 0.68);
            vec3 ionCyan = vec3(0.04, 0.72, 0.88);
            vec3 ultraviolet = vec3(0.36, 0.07, 0.58);
            color = mix(deepSpace, ionBlue, smoothstep(0.34, 0.76, body));
            color = mix(color, ultraviolet, smoothstep(0.5, 0.84, innerBillow) * (0.35 + warp.x * 0.25));
            color += ionCyan * litEdge * 0.74;
            color = mix(color, spectralColor, 0.48 * smoothstep(0.27, 0.8, body));
            color *= 0.36 + volume * 0.96 + farCloud * 0.12;
            alpha = (volume * 0.82 + litEdge * 0.38 + farCloud * 0.08)
              * centralReserve * edgeFade;
          } else {
            float radius = length(centered);
            vec2 tilted = mat2(0.82, -0.57, 0.57, 0.82) * centered;
            vec2 p = tilted * vec2(2.28, 3.32);
            float farCloud = fbm(p * 0.48 + vec2(-time * 0.2, time * 0.34));
            vec2 warp = vec2(
              fbm(p * 0.65 + vec2(5.8, time * 0.52)),
              fbm(p * 0.65 + vec2(-time * 0.44, -7.2))
            ) - 0.5;
            vec2 radialDirection = normalize(centered + vec2(0.0001));
            vec2 tangent = vec2(-radialDirection.y, radialDirection.x);
            float flowSignal = fbm(tilted * 1.18 + vec2(time * 0.18, -time * 0.11));
            vec2 cloudP = p + warp * 2.08
              + radialDirection * (flowSignal - 0.5) * 0.24
              + tangent * (flowSignal - 0.5) * 0.18
              + vec2(-time * 0.22, time * 0.42);
            float body = fbm(cloudP);
            float folded = fbm(cloudP * 2.08 - warp * 1.1);
            float filament = ridged(cloudP * vec2(3.4, 2.45) + vec2(time * 0.62, 0.0));
            float microDetail = fbm(cloudP * 5.4 - warp * 2.0);
            float dustLane = smoothstep(0.5, 0.73, fbm(cloudP * 1.28 + vec2(-5.3, 9.1)));
            float rift = 1.0 - smoothstep(0.06, 0.54, abs(tilted.y + warp.x * 0.34));
            float volume = smoothstep(
              0.31 - rightCloudLift * 0.055,
              0.69 - rightCloudLift * 0.035,
              body * 0.61 + folded * 0.3 + farCloud * 0.25
            );
            volume *= 0.48 + rift * 0.72;
            volume *= mix(1.0, 0.24, dustLane * smoothstep(0.4, 0.8, folded));
            float litEdge = filament * rift * smoothstep(0.38, 0.76, body) * (0.48 + microDetail * 0.52);

            vec3 neighboringColor = hsv2rgb(vec3(fract(localHue + 0.09), 0.78, 0.78));
            vec3 coldVoid = spectralColor * 0.075;
            color = mix(coldVoid, spectralColor, smoothstep(0.31, 0.76, body));
            color = mix(color, neighboringColor, smoothstep(0.5, 0.86, folded) * 0.34);
            color += mix(spectralColor, neighboringColor, flowSignal) * litEdge * 0.72;
            color *= 0.34 + volume * 0.96 + farCloud * 0.12;
            alpha = (volume * 0.84 + litEdge * 0.42 + farCloud * 0.07)
              * centralReserve * edgeFade;
          }

          float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
          color = mix(vec3(luminance), color, 0.58) * 1.07;
          color = color / (vec3(1.0) + color * 1.4);
          alpha *= 0.515;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    const nebula = new THREE.Mesh(geometry, material);
    nebula.position.z = -9;
    nebula.renderOrder = -20;
    nebula.frustumCulled = false;
    return nebula;
  }

  private animate = (): void => {
    this.raf = requestAnimationFrame(this.animate);
    const delta = Math.min(this.clock.getDelta(), 0.05);
    const elapsed = this.clock.elapsedTime;
    const rawBands = this.metrics?.bands ?? [0, 0, 0, 0, 0, 0, 0, 0];
    const playbackActive = this.nebulaPlaying || this.previewing;

    const motionScale = this.reducedMotion ? 0.12 : 1;
    this.uniforms.uTime.value = elapsed * motionScale;
    const playbackTarget = playbackActive ? 1 : 0;
    const reactionEase = 1 - Math.exp(-delta * (playbackActive ? 1.45 : 0.9));
    this.reactionBlend += (playbackTarget - this.reactionBlend) * reactionEase;
    if (!playbackActive && this.reactionBlend < 0.0005) this.reactionBlend = 0;
    const shapedBlend = this.reactionBlend * this.reactionBlend * (3 - 2 * this.reactionBlend);
    if (playbackActive) {
      for (let bandIndex = 0; bandIndex < this.reactiveBands.length; bandIndex += 1) {
        const target = rawBands[bandIndex] * shapedBlend;
        if (this.reactionBlend > 0.995) {
          this.reactiveBands[bandIndex] = rawBands[bandIndex];
        } else {
          const gatherEase = 1 - Math.exp(-delta * 4.2);
          this.reactiveBands[bandIndex] += (target - this.reactiveBands[bandIndex]) * gatherEase;
        }
      }
    } else {
      const releaseDecay = Math.exp(-delta * 1.12);
      for (let bandIndex = 0; bandIndex < this.reactiveBands.length; bandIndex += 1) {
        this.reactiveBands[bandIndex] *= releaseDecay;
        if (this.reactiveBands[bandIndex] < 0.0004) this.reactiveBands[bandIndex] = 0;
      }
    }
    const bands = this.reactiveBands;
    this.uniforms.uBands.value = bands;
    const playbackEase = 1 - Math.exp(-delta * (playbackActive ? 1.15 : 0.72));
    this.nebulaPlaybackBlend += (playbackTarget - this.nebulaPlaybackBlend) * playbackEase;
    const ambientSpeed = 0.42 + this.nebulaPlaybackBlend * 0.78;
    this.nebulaTimeUniform.value += delta * ambientSpeed * motionScale;

    let cueEnergy = 0;
    let cueOnset = 0;
    for (let bandIndex = 2; bandIndex < bands.length; bandIndex += 1) {
      cueEnergy = Math.max(cueEnergy, bands[bandIndex]);
      cueOnset = Math.max(cueOnset, bands[bandIndex] - this.previousOrbCueBands[bandIndex]);
      this.previousOrbCueBands[bandIndex] = bands[bandIndex];
    }
    const floorRate = cueEnergy > this.orbCueFloor ? 1.1 : 3.2;
    this.orbCueFloor += (cueEnergy - this.orbCueFloor) * Math.min(1, delta * floorRate);
    const cueLift = Math.max(0, cueEnergy - this.orbCueFloor);
    this.orbCueCooldown = Math.max(0, this.orbCueCooldown - delta);
    if (this.orbCueCooldown === 0 && cueEnergy > 0.1 && cueOnset > 0.01 && cueLift > 0.02) {
      this.orbDeformationEnvelope = 1;
      this.orbCueCooldown = 0.08;
    } else {
      this.orbDeformationEnvelope *= Math.exp(-delta * 4.8);
      if (this.orbDeformationEnvelope < 0.001) this.orbDeformationEnvelope = 0;
    }
    this.uniforms.uOrbMorph.value = this.settings.orbMorph * this.orbDeformationEnvelope;

    if (this.gyrationEnabled) {
      const reducedMotionScale = this.reducedMotion ? 0.28 : 1;
      this.gyrationProgress = Math.min(1, this.gyrationProgress + delta * reducedMotionScale / this.gyrationDuration);
      const eased = this.gyrationProgress * this.gyrationProgress * (3 - 2 * this.gyrationProgress);
      this.rotationTarget.x = THREE.MathUtils.lerp(this.gyrationFrom.x, this.gyrationTo.x, eased);
      this.rotationTarget.y = THREE.MathUtils.lerp(this.gyrationFrom.y, this.gyrationTo.y, eased);
      if (this.gyrationProgress >= 1) {
        this.gyrationFrom = { ...this.gyrationTo };
        this.chooseGyrationDestination();
        this.gyrationProgress = 0;
      }
    }
    const targetX = this.rotationTarget.x + this.hoverAttitude.x;
    const targetY = this.rotationTarget.y + this.hoverAttitude.y;
    this.diskGroup.rotation.x += (targetX - this.diskGroup.rotation.x) * Math.min(1, delta * (this.spatialPreview ? 3.6 : 4.8));
    this.diskGroup.rotation.y += (targetY - this.diskGroup.rotation.y) * Math.min(1, delta * (this.spatialPreview ? 3.6 : 4.8));
    this.diskGroup.rotation.z = Math.sin(elapsed * 0.04) * 0.012 * motionScale;
    if (this.centerOrb) {
      const orbEnergy = bands.reduce((total, bandEnergy) => total + bandEnergy, 0) / bands.length;
      this.centerOrb.rotation.y += delta * (0.07 + orbEnergy * 0.52) * motionScale;
      this.centerOrb.rotation.x = Math.sin(elapsed * 0.09) * 0.065 * motionScale
        - (this.spatialPreview ? this.diskGroup.rotation.x * 0.34 : 0);
      this.centerOrb.rotation.z = this.spatialPreview ? -this.diskGroup.rotation.y * 0.22 : 0;
    }
    this.renderer.render(this.scene, this.camera);
  };

  private bindInteraction(): void {
    this.container.addEventListener('pointerdown', (event) => {
      if ((event.target as Element).closest('button, input, select, label, aside, nav')) return;
      this.dragging = true;
      this.pointer = { x: event.clientX, y: event.clientY };
      this.container.setPointerCapture(event.pointerId);
    });
    this.container.addEventListener('pointermove', (event) => {
      if (this.spatialPreview && !this.dragging) {
        const bounds = this.container.getBoundingClientRect();
        const normalizedX = (event.clientX - bounds.left) / Math.max(1, bounds.width) * 2 - 1;
        const normalizedY = (event.clientY - bounds.top) / Math.max(1, bounds.height) * 2 - 1;
        this.hoverAttitude.x = THREE.MathUtils.clamp(normalizedY * 0.055, -0.055, 0.055);
        this.hoverAttitude.y = THREE.MathUtils.clamp(normalizedX * 0.09, -0.09, 0.09);
        return;
      }
      if (!this.dragging) return;
      const deltaX = event.clientX - this.pointer.x;
      const deltaY = event.clientY - this.pointer.y;
      this.pointer = { x: event.clientX, y: event.clientY };
      const horizontalLimit = this.spatialPreview ? 0.78 : 0.48;
      const minTilt = this.spatialPreview ? -0.42 : -0.2;
      const maxTilt = this.spatialPreview ? 0.9 : 0.42;
      this.rotationTarget.y = THREE.MathUtils.clamp(this.rotationTarget.y + deltaX * (this.spatialPreview ? 0.0038 : 0.003), -horizontalLimit, horizontalLimit);
      this.rotationTarget.x = THREE.MathUtils.clamp(this.rotationTarget.x + deltaY * (this.spatialPreview ? 0.0032 : 0.0024), minTilt, maxTilt);
    });
    const release = (): void => { this.dragging = false; };
    this.container.addEventListener('pointerup', release);
    this.container.addEventListener('pointercancel', release);
    this.container.addEventListener('pointerleave', () => {
      if (this.spatialPreview && !this.dragging) this.hoverAttitude = { x: 0, y: 0 };
    });
    this.container.addEventListener('dblclick', () => this.resetAttitude());
  }

  private resize(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (!width || !height) return;
    this.renderer.setSize(width, height, false);
    const aspect = width / height;
    this.camera.aspect = aspect;
    this.camera.position.z = aspect < 0.76 ? 17.7 : aspect < 1.15 ? 12.4 : 8.8;
    this.camera.updateProjectionMatrix();
    const desktop = width > 900;
    const leftRail = desktop ? Math.min(366, Math.max(300, width * 0.219)) : 0;
    const rightRail = desktop ? Math.min(304, Math.max(270, width * 0.182)) : 0;
    const pixelOffset = (leftRail - rightRail) / 2;
    const visibleWidth = 2 * this.camera.position.z * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)) * aspect;
    this.diskGroup.position.x = pixelOffset / width * visibleWidth;
    this.uniforms.uPixelRatio.value = this.renderer.getPixelRatio();
  }

  private random(seed: number): number {
    const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
    return value - Math.floor(value);
  }

  private particleVertexShader(): string {
    return /* glsl */`
      attribute float aRadius;
      attribute float aAngle;
      attribute float aSeed;
      attribute float aBand;
      attribute float aSize;
      uniform float uTime;
      uniform float uBands[8];
      uniform float uIntensity;
      uniform float uDistortion;
      uniform float uTravel;
      uniform float uPixelRatio;
      uniform float uSpatialDepth;
      varying float vBand;
      varying float vAlpha;

      float readBand(float index) {
        if (index < 0.5) return uBands[0];
        if (index < 1.5) return uBands[1];
        if (index < 2.5) return uBands[2];
        if (index < 3.5) return uBands[3];
        if (index < 4.5) return uBands[4];
        if (index < 5.5) return uBands[5];
        if (index < 6.5) return uBands[6];
        return uBands[7];
      }

      float hash31(vec3 point) {
        point = fract(point * 0.1031);
        point += dot(point, point.yzx + 33.33);
        return fract((point.x + point.y) * point.z);
      }

      float valueNoise(vec3 point) {
        vec3 cell = floor(point);
        vec3 blend = fract(point);
        blend = blend * blend * (3.0 - 2.0 * blend);

        float lower = mix(
          mix(hash31(cell), hash31(cell + vec3(1.0, 0.0, 0.0)), blend.x),
          mix(hash31(cell + vec3(0.0, 1.0, 0.0)), hash31(cell + vec3(1.0, 1.0, 0.0)), blend.x),
          blend.y
        );
        float upper = mix(
          mix(hash31(cell + vec3(0.0, 0.0, 1.0)), hash31(cell + vec3(1.0, 0.0, 1.0)), blend.x),
          mix(hash31(cell + vec3(0.0, 1.0, 1.0)), hash31(cell + vec3(1.0, 1.0, 1.0)), blend.x),
          blend.y
        );
        return mix(lower, upper, blend.z);
      }

      float fbm(vec3 point) {
        float total = 0.0;
        float amplitude = 0.57;
        for (int octave = 0; octave < 3; octave++) {
          total += valueNoise(point) * amplitude;
          point = point * 2.03 + vec3(17.1, 9.2, 13.4);
          amplitude *= 0.5;
        }
        return total / 0.9975;
      }

      void main() {
        float bandEnergy = readBand(aBand);
        float velocity = 0.035 + 0.075 / max(0.8, aRadius - 1.0);
        float theta = aAngle + uTime * velocity;
        float bassWeight = 1.0 - smoothstep(0.5, 2.75, aBand);
        float responseCurve = mix(1.08, 0.82, bassWeight);
        float response = pow(clamp(bandEnergy, 0.0, 1.0), responseCurve);
        float responseAuthority = 1.0 + bassWeight * 0.58;
        float radialTravel = response * (0.16 + aSeed * 0.1) * 1.1 * uTravel * 1.65 * responseAuthority;
        float dance = sin(aSeed * 31.0 + uTime * (1.2 + aBand * 0.11)) * response * 0.012;
        float bandPosition = aBand / 7.0;
        float noiseScale = mix(0.72, 2.35, pow(bandPosition, 0.82));
        vec3 noisePosition = vec3(
          cos(theta) * noiseScale,
          sin(theta) * noiseScale,
          aRadius * 1.35 + uTime * (0.1 + bandPosition * 0.045)
        );
        float shapeNoise = fbm(noisePosition);
        float depthNoise = fbm(noisePosition * 1.19 + vec3(4.7, -3.2, 2.1));
        float distortion = uDistortion * response;
        float coherentLobe = shapeNoise * 1.25 - 0.36;
        float radialWarp = coherentLobe * distortion * mix(0.32, 0.13, bandPosition);
        float radius = aRadius + radialTravel + dance + radialWarp;
        float thickness = (aSeed - 0.5) * (0.045 + response * 0.12)
          + (depthNoise * 2.0 - 1.0) * distortion * mix(0.16, 0.06, bandPosition);
        thickness *= uSpatialDepth;
        vec3 pos = vec3(cos(theta) * radius, sin(theta) * radius, thickness);
        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        gl_PointSize = (aSize + 0.64 + response * 2.05) * uPixelRatio * uIntensity * (8.8 / -mvPosition.z);
        vBand = aBand;
        vAlpha = 0.54 + response * 0.46;
      }
    `;
  }

  private centerOrbVertexShader(): string {
    return /* glsl */`
      attribute float aSeed;
      attribute float aBand;
      attribute float aSize;
      uniform float uTime;
      uniform float uBands[8];
      uniform float uIntensity;
      uniform float uOrbSize;
      uniform float uOrbMorph;
      uniform float uOrbResponse;
      uniform float uOrbTurbulence;
      uniform float uOrbBassPulse;
      uniform float uOrbCohesion;
      uniform float uOrbTopology;
      uniform float uOrbFragmentation;
      uniform float uPixelRatio;
      varying float vBand;
      varying float vAlpha;
      varying float vCloudDepth;

      float readBand(float index) {
        if (index < 0.5) return uBands[0];
        if (index < 1.5) return uBands[1];
        if (index < 2.5) return uBands[2];
        if (index < 3.5) return uBands[3];
        if (index < 4.5) return uBands[4];
        if (index < 5.5) return uBands[5];
        if (index < 6.5) return uBands[6];
        return uBands[7];
      }

      float hash31(vec3 point) {
        point = fract(point * 0.1031);
        point += dot(point, point.yzx + 33.33);
        return fract((point.x + point.y) * point.z);
      }

      float valueNoise(vec3 point) {
        vec3 cell = floor(point);
        vec3 blend = fract(point);
        blend = blend * blend * (3.0 - 2.0 * blend);
        float lower = mix(
          mix(hash31(cell), hash31(cell + vec3(1.0, 0.0, 0.0)), blend.x),
          mix(hash31(cell + vec3(0.0, 1.0, 0.0)), hash31(cell + vec3(1.0, 1.0, 0.0)), blend.x),
          blend.y
        );
        float upper = mix(
          mix(hash31(cell + vec3(0.0, 0.0, 1.0)), hash31(cell + vec3(1.0, 0.0, 1.0)), blend.x),
          mix(hash31(cell + vec3(0.0, 1.0, 1.0)), hash31(cell + vec3(1.0, 1.0, 1.0)), blend.x),
          blend.y
        );
        return mix(lower, upper, blend.z);
      }

      float fbm(vec3 point) {
        float total = 0.0;
        float amplitude = 0.57;
        for (int octave = 0; octave < 3; octave++) {
          total += valueNoise(point) * amplitude;
          point = point * 2.03 + vec3(17.1, 9.2, 13.4);
          amplitude *= 0.5;
        }
        return total / 0.9975;
      }

      void main() {
        float bandEnergy = readBand(aBand);
        float bandPosition = aBand / 7.0;
        float bassWeight = 1.0 - smoothstep(0.5, 2.75, aBand);
        float response = min(1.8, pow(clamp(bandEnergy, 0.0, 1.0), mix(1.05, 0.8, bassWeight)) * uOrbResponse);
        float bassEnergy = max(uBands[0], uBands[1]);
        float baseRadius = length(position);
        vec3 normal = normalize(position + vec3(0.0001));
        float flowTime = uTime * (0.075 + bandPosition * 0.025);
        float baseNoiseScale = mix(1.8, 4.3, uOrbTurbulence);
        float noiseScale = mix(baseNoiseScale, baseNoiseScale * 0.72, uOrbMorph);
        vec3 noisePosition = position * noiseScale + vec3(aSeed * 0.42);
        vec3 flow = vec3(
          fbm(noisePosition + vec3(flowTime, 7.1, 1.9)),
          fbm(noisePosition + vec3(3.7, -flowTime * 0.82, 9.2)),
          fbm(noisePosition + vec3(8.4, 2.6, flowTime * 0.67))
        ) * 2.0 - 1.0;
        float lobeScale = mix(0.85, 2.05, uOrbTurbulence);
        float lobe = fbm(normal * lobeScale + vec3(flowTime * 0.7, -flowTime * 0.44, 4.1)) * 2.0 - 1.0;
        float morph = uOrbMorph * (0.31 + response * mix(0.3, 0.16, bandPosition));
        float looseness = mix(1.35, 0.65, uOrbCohesion);
        float volumeDrift = (0.07 + response * 0.11) * uOrbMorph * looseness;
        float breathing = sin(uTime * 0.31 + aSeed * 6.2831) * 0.012 * uOrbMorph;
        float beatImpulse = pow(clamp(bassEnergy, 0.0, 1.0), 1.85) * uOrbResponse * uOrbBassPulse * 0.18;
        float cluster = floor(aSeed * 5.0);
        float clusterAngle = cluster * 1.256637 + flowTime * mix(-0.8, 0.8, step(2.5, cluster));
        vec3 clusterDirection = normalize(vec3(
          cos(clusterAngle),
          sin(cluster * 2.17 + flowTime * 0.46) * 0.72,
          sin(clusterAngle)
        ));
        float clusterDrift = (0.055 + response * 0.045) * uOrbMorph * looseness;
        vec3 tangentFlow = flow - normal * dot(flow, normal);
        float shellWeight = smoothstep(0.15, 0.58, baseRadius);
        float cohesionBias = (uOrbCohesion - 0.5) * 2.0;
        vec3 pos = position * (1.0 - cohesionBias * shellWeight * 0.16);
        pos *= 1.0 + lobe * morph + breathing;
        pos += tangentFlow * volumeDrift;
        pos += clusterDirection * clusterDrift;
        pos += normal * (response * mix(0.038, 0.014, bandPosition) + beatImpulse);
        float shellBlend = smoothstep(0.0, 0.52, uOrbTopology);
        vec3 shellPosition = normalize(pos + vec3(0.0001)) * mix(0.48, 0.58, bandPosition);
        pos = mix(pos, shellPosition, shellBlend);
        float azimuth = atan(position.z, position.x);
        float tubeAngle = aSeed * 6.283185 + aBand * 0.67;
        float torusMajor = 0.43;
        float torusMinor = mix(0.08, 0.17, bandPosition);
        vec3 torusPosition = vec3(
          cos(azimuth) * (torusMajor + cos(tubeAngle) * torusMinor),
          sin(azimuth) * (torusMajor + cos(tubeAngle) * torusMinor),
          sin(tubeAngle) * torusMinor
        );
        pos = mix(pos, torusPosition, smoothstep(0.48, 1.0, uOrbTopology));
        pos += clusterDirection * uOrbFragmentation * (0.08 + 0.18 * smoothstep(0.22, 1.0, aSeed));
        float radius = length(pos);
        float boundary = mix(0.94, 0.76, uOrbCohesion) + lobe * 0.035;
        if (radius > boundary) pos *= boundary / radius;
        pos *= uOrbSize;
        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        float depthScale = 1.0 - smoothstep(0.42, 0.86, length(pos));
        gl_PointSize = (aSize + 0.72 + response * 1.35 + depthScale * 0.42) * uPixelRatio * uIntensity * (8.8 / -mvPosition.z);
        vBand = aBand;
        vAlpha = 0.15 + response * 0.38 + depthScale * 0.16 + aSeed * 0.045;
        vCloudDepth = depthScale;
      }
    `;
  }

  private centerOrbFragmentShader(): string {
    return /* glsl */`
      precision highp float;
      uniform float uBandHues[8];
      varying float vBand;
      varying float vAlpha;
      varying float vCloudDepth;

      float readBandHue(float index) {
        if (index < 0.5) return uBandHues[0];
        if (index < 1.5) return uBandHues[1];
        if (index < 2.5) return uBandHues[2];
        if (index < 3.5) return uBandHues[3];
        if (index < 4.5) return uBandHues[4];
        if (index < 5.5) return uBandHues[5];
        if (index < 6.5) return uBandHues[6];
        return uBandHues[7];
      }

      vec3 hueToRgb(float hue) {
        vec3 channels = abs(fract(hue + vec3(0.0, 0.666667, 0.333333)) * 6.0 - 3.0);
        return clamp(channels - 1.0, 0.0, 1.0);
      }

      void main() {
        float radius = length(gl_PointCoord - 0.5);
        if (radius > 0.5) discard;
        float core = exp(-radius * radius * 19.0);
        float haze = 1.0 - smoothstep(0.08, 0.5, radius);
        float invertedHue = fract(readBandHue(vBand) + 0.5);
        vec3 color = mix(vec3(1.0), hueToRgb(invertedHue), 0.84);
        color = mix(color, vec3(0.88, 0.96, 1.0), vCloudDepth * 0.32);
        float alpha = (core * 0.72 + haze * 0.28) * vAlpha;
        gl_FragColor = vec4(color, alpha);
      }
    `;
  }

  private particleFragmentShader(): string {
    return /* glsl */`
      precision highp float;
      uniform float uBandHues[8];
      varying float vBand;
      varying float vAlpha;

      float readBandHue(float index) {
        if (index < 0.5) return uBandHues[0];
        if (index < 1.5) return uBandHues[1];
        if (index < 2.5) return uBandHues[2];
        if (index < 3.5) return uBandHues[3];
        if (index < 4.5) return uBandHues[4];
        if (index < 5.5) return uBandHues[5];
        if (index < 6.5) return uBandHues[6];
        return uBandHues[7];
      }

      vec3 hueToRgb(float hue) {
        vec3 channels = abs(fract(hue + vec3(0.0, 0.666667, 0.333333)) * 6.0 - 3.0);
        return clamp(channels - 1.0, 0.0, 1.0);
      }

      void main() {
        float distanceToCenter = length(gl_PointCoord - 0.5);
        if (distanceToCenter > 0.5) discard;
        float edge = 1.0 - smoothstep(0.32, 0.5, distanceToCenter);
        vec3 pureHue = hueToRgb(readBandHue(vBand));
        vec3 color = mix(vec3(1.0), pureHue, 0.82) * 0.96;
        gl_FragColor = vec4(color, edge * vAlpha);
      }
    `;
  }
}
